import { CobaltLink } from '@cae/cobalt-react/components/link';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconExternalLinkOutline } from '@cae/cobalt-react/icons/outline/CobaltIconExternalLinkOutline';
import { useProfile } from '@features/profile/hooks/use-profile/useProfile';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import * as React from 'react';

import { passwordReset } from './helpers/passwordReset';
import { PasswordResetModal } from './password-reset/PasswordResetModal';
import styles from './PasswordAndSecurity.module.css';
import { PASSWORD_RESET_STORAGE_KEY } from './PasswordAndSecurity.utils';

const PasswordAndSecurity = React.memo((): React.ReactElement => {
  const { profile, isProfileError, isProfileLoading } = useProfile();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] =
    React.useState(false);
  const loginHint = profile?.email || '';

  const handleResetPasswordClick = React.useCallback((): void => {
    if (localStorage.getItem(PASSWORD_RESET_STORAGE_KEY) === 'true') {
      passwordReset(loginHint);
      return;
    }
    setResetPasswordModalOpen(true);
  }, [loginHint]);

  const handleCloseResetPasswordModal = React.useCallback((): void => {
    setResetPasswordModalOpen(false);
  }, []);

  if (isProfileLoading) return <ContentLoader />;
  if (isProfileError || loginHint === '') return <InlineFeedback type="data-error"/>;

  return (
    <div className={styles.wrapper} data-heap-id="password-and-security">
      <div className={styles.scrollArea}>
        {!isMobile && <h2 className="heading-02">Password and security</h2>}
        <div className={styles.section}>
          <div className={styles.content}>
            <span className={`body-02 ${styles.title}`}>Password</span>
            <span>
              To reset your global CAE Account password, click the link below
              and follow the steps.
            </span>
          </div>
          <CobaltLink
            data-heap-id="profile-password-reset-link"
            variant="primary"
            size="small"
            onClick={handleResetPasswordClick}
            aria-label="Reset CAE Account password"
          >
            Reset password
            <CobaltIconExternalLinkOutline />
          </CobaltLink>
        </div>
        <div className={styles.section}>
          <div className={styles.content}>
            <span className={`body-02 ${styles.title}`}>Delete account</span>
            <span>
              To request account deletion, please contact CAE Customer Care.
            </span>
          </div>
          <CobaltLink
            data-heap-id="profile-password-delete-account-link"
            variant="primary"
            size="small"
            onClick={navigateToHelp}
            aria-label="Submit a request to delete account"
          >
            Submit a request
            <CobaltIconExternalLinkOutline />
          </CobaltLink>
        </div>
      </div>
      <PasswordResetModal
        isModalOpen={resetPasswordModalOpen}
        onClose={handleCloseResetPasswordModal}
        loginHint={loginHint}
      />
    </div>
  );
});

PasswordAndSecurity.displayName = 'PasswordAndSecurity';
export { PasswordAndSecurity };
